import React from "react";
import loadable from "@loadable/component";
import clsx from "clsx";
import useSystemLinks from "@micado-digital/react-ui/hooks/useSystemLinks";
import useWeatherAPI from "@micado-digital/react-weather/hooks/useWeatherAPI";
import { useInView } from "react-intersection-observer";
import useLocals from "@micado-digital/react-ui/hooks/useLocals";
import localDE from "@localDE";
import localEN from "@localEN";
import localNL from "@localNL";

import styles from "./WeatherWebcam.styles";

const Container = loadable(() => import("@Components/views/Container"));
const ReactLink = loadable(() => import("@micado-digital/react-link/ReactLink"));
const ReactSVGIcon = loadable(() => import("@micado-digital/react-svg-icon/ReactSVGIcon"));
const Typography = loadable(() => import("@micado-digital/react-ui/components/Typography"));
const Button = loadable(() => import("@material-ui/core/Button"));
const IframeResizer = loadable(() => import("iframe-resizer-react"));

const WeatherWebcam = ({ station, lang, webcam, webcamTitle }) => {
	const { REACT_APP_PATH } = process.env;
	const css = styles();
	const sysLink = useSystemLinks();
	const [ref, inView] = useInView({
		rootMargin: "200px", // adjust this value as needed
		triggerOnce: true
	});

	const webcamSrc = inView ? webcam : "";

	const l = useLocals({
		lang: lang,
		res: {
			de: localDE,
			en: localEN,
			nl: localNL
		}
	});

	const url = `${REACT_APP_PATH}/Micado.Tourism.Web/Micado.Tourism.Web.IO.Api.WeatherApi/GetDetailsAsJson.api`;

	const data = useWeatherAPI(0, lang, station, url)?.[0];

	const getDayLabel = type => {
		switch (type) {
			case "morning":
				return l("weather.morning");
			case "afternoon":
				return l("weather.afternoon");
			case "evening":
				return l("weather.evening");
			case "night":
				return l("weather.night");
			default:
				return "";
		}
	};

	if (data?.length === 0 || data?.loading) {
		return <></>;
	}

	const { days = [], name } = data?.[0];

	return (
		<div className={clsx(css.root, "mco-weather-webcam")} ref={ref}>
			<Container>
				<div className={css.weather}>
					<Typography className={css.headline} variant="headline-large">
						{l("weather.headline")}
					</Typography>

					{name && (
						<Typography
							className={css.title}
							dangerouslySetInnerHTML={{ __html: name }}
							variant="title-large"
						/>
					)}
					{days?.[0]?.text && (
						<Typography
							className={css.text}
							dangerouslySetInnerHTML={{ __html: days?.[0]?.text }}
							variant="body-small"
						/>
					)}
					<div className={css.wrapper}>
						{days?.[0]?.times?.map((item, index) => {
							return (
								<div key={index}>
									{item?.type && (
										<Typography
											className={css.timeLabel}
											dangerouslySetInnerHTML={{ __html: getDayLabel(item?.type) }}
											variant="title-small"
										/>
									)}
									<div className={css.timeWrapper}>
										<ReactSVGIcon
											className={css.icon}
											size={72}
											src={`/img/icons/weather/${item?.state}.svg`}
										/>
										<Typography className={css.temp} variant="headline-medium">
											{item?.temperature}°C
										</Typography>
									</div>
								</div>
							);
						})}
					</div>
					<ReactLink to={sysLink("weather-detail")}>
						<Button
							color="secondary"
							className={css.button}
							variant="outlined"
							endIcon={
								<ReactSVGIcon color="primary" size={24} src="/img/icons/chevron-right.svg" />
							}
						>
							{l("weather.button")}
						</Button>
					</ReactLink>
				</div>
				<div className={css.webcam}>
					<Typography className={css.headlineWebcam} variant="headline-large">
						{l("weather.headlineWebcam")}
					</Typography>
					<div className={css.webcamWrapper}>
						{webcamTitle && (
							<Typography className={css.webcamTitle} variant="title-small">
								{webcamTitle}
							</Typography>
						)}
						<IframeResizer
							allowFullScreen
							frameBorder="0"
							src={webcamSrc}
							style={{ height: "100%", maxHeight: 255, maxWidth: "100%", width: "100%" }}
						/>
					</div>
				</div>
			</Container>
		</div>
	);
};

export default WeatherWebcam;
